/**
 * PersonelRosterEylemleri.jsx — Daimî personel listesi (`person_to_firma`) SATIRININ eylemleri ve
 * iki onay penceresi. Backlog #108 (backend #65 sonlandırma, #109 geri alma).
 *
 * Kullananlar, hepsi AYNI görünsün diye tek yerde:
 *   · Components/Kisi/FirmaPersonelSekmeleri.jsx  (kart satırı — firma portalı /Firma/Personel VE denetmen Firma Detay > Personel Listesi)
 * #357'ye kadar bu düğmeler üç yerde kullanılırdı: denetmen FirmaRosterPanel (tablo), firma FirmaDaimiListe (kart) ve firma
 * Fuar Kadrosu kartı. İki "Daimî Liste" görünümü kalktı (o liste artık Tüm Personeller sekmesi); hepsi tek sekmeli bileşende.
 * Firma portalındaki ayrı "Personel Listem" sayfası (/Firma/PersonelListem) #300'de kalktı.
 *
 * Satır ŞEKLİ = liste satırı (`rosterId`/`aktif`/`fuaraAtanabilir`/`geriAlinabilir`), kadro satırı
 * DEĞİL (bkz. PersonelKartParcalari.jsx'in başındaki uyarı). Kararların hepsi sunucudan hazır gelir:
 *   · `fuaraAtanabilir` → "Fuara Görevlendir" görünür mü
 *   · `geriAlinabilir`  → "Geri Al" görünür mü (çağırana göre: firma yalnızca KENDİ sonlandırmasını,
 *                          SAKA hepsini; aktif satırda hep false)
 * İstemci bu kuralları yeniden kurmuyor — yalnızca `aktif === false` olan satırı "sonlandırılmış" diye
 * soluk basıyor ve eylem hücresini ona göre seçiyor.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Input, Label, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { UserCheck, UserMinus, Undo2, AlertTriangle } from "lucide-react";
import { iliskiBitisMeta, BITIS_SEBEBI_MAX } from "../../services/personelRosterService";
import { IliskiBitisRozeti } from "./PersonelKartParcalari";
import { useTranslation } from "react-i18next";

/** Sonlandırılmış satırın veri hücreleri soluk basılır; eylem hücresi ve rozet net kalır. */
export function rosterSatirStili(row) {
  return iliskiBitisMeta(row).sonlandirildi ? { opacity: 0.6 } : undefined;
}

/**
 * Personel KARTININ sağ düğme sütunu (Detaylar · İlişkiyi Sonlandır · Geri Al) için sabit genişlik —
 * "Onayla" ile "Reddet" yan yana durunca sütunun aldığı genişlik. Onay/red düğmesi olmayan kartlarda da
 * aynı kalır, böylece kartlardaki sayaç/belge kareleri aynı hizada durur. İki kart türü (Fuar Kadrosu,
 * Daimî Liste) ve düğmeler bu sabiti paylaşır.
 */
export const KART_EYLEM_SUTUNU = { width: 188 };

/** Kartın "Çalışan" rozetinin altındaki yeşil düğme — kadro kartında pasif (kişi zaten görevli), listede aktif. */
export function KartGorevlendirDugmesi({ onClick, disabled, title, tam = false }) {
  const { t } = useTranslation("components");
  // `tam` (#728): sağdaki düğme yığınında (Kişi Listesi kartı) diğer düğmelerle aynı genişlikte dursun.
  return (
    <span title={title} className={tam ? "d-block" : "d-inline-block"} data-tour="personel-gorevlendir">
      <Button size="sm" color="green" className={tam ? "w-100" : undefined} disabled={disabled} onClick={onClick}>
        <UserCheck size={14} className="me-1" />{t("rosterEylemleri.fuaraGorevlendir")}
      </Button>
    </span>
  );
}

/**
 * "Görevlendirmeyi Kaldır" (Yabancı Katılımcı çizimi): kişiyi YALNIZCA bu fuarın kadrosundan çıkarır, firmanın personel listesindeki kaydına
 * dokunmaz — "İlişkiyi Sonlandır"dan ayrı bir iş. Kırmızı çerçeveli düğme; onay penceresini çağıran açar.
 */
export function KartGorevKaldirDugmesi({ onClick, disabled }) {
  const { t } = useTranslation("components");
  return (
    <span title={t("rosterEylemleri.gorevlendirmeyiKaldirTitle")} className="d-inline-block" data-tour="personel-gorev-kaldir">
      <Button size="sm" color="danger" outline disabled={disabled} onClick={onClick}>
        <UserMinus size={14} className="me-1" />{t("rosterEylemleri.gorevlendirmeyiKaldir")}
      </Button>
    </span>
  );
}

/**
 * Kartın sağ sütununun en altındaki kırmızı düğme (sütun genişliğinde).
 * `sade` (#511, Yabancı Katılımcı): kırmızı düğme yerine sönük, küçük bir metin bağlantısı — misafirlerin gözüne çarpmasın;
 * tıklayınca yine aynı onay penceresi (neden + onay) açılır.
 */
export function KartSonlandirDugmesi({ onClick, disabled, sade = false }) {
  const { t } = useTranslation("components");
  if (sade) {
    return (
      <Button
        color="link" size="sm" className="w-100 text-muted fw-normal py-0 shadow-none" disabled={disabled}
        style={{ fontSize: "0.75rem", textDecoration: "underline" }}
        title={t("rosterEylemleri.buFirmaIleIliskiyiSonlandir")}
        onClick={onClick}
      >
        {t("rosterEylemleri.iliskiyiSonlandir")}
      </Button>
    );
  }
  return (
    <Button
      size="sm" color="danger" className="w-100" disabled={disabled}
      title={t("rosterEylemleri.buFirmaIleIliskiyiSonlandir")}
      onClick={onClick}
    >
      <UserMinus size={14} className="me-1" />{t("rosterEylemleri.iliskiyiSonlandir")}
    </Button>
  );
}

/** Sonlandırılmış satırda, `geriAlinabilir` true ise sütun genişliğinde "Geri Al". */
export function KartGeriAlDugmesi({ onClick, disabled }) {
  const { t } = useTranslation("components");
  return (
    <Button
      size="sm" color="warning" outline className="w-100" disabled={disabled}
      title={t("rosterEylemleri.sonlandirilanIliskiyiGeriAl")}
      onClick={onClick}
    >
      <Undo2 size={14} className="me-1" />{t("rosterEylemleri.geriAl")}
    </Button>
  );
}

/**
 * "Kişi" hücresi: ad, yasak/sonlandırıldı rozetleri, kişi id ve (varsa) kim/ne zaman/neden.
 * #384: #264'ün "Sigortalı: X" satırı kalktı (sigortalı firma kavramı backend #383'le bitti).
 */
export function RosterKisiHucresi({ row }) {
  const { t } = useTranslation("components");
  const bitis = iliskiBitisMeta(row);
  return (
    <>
      <div className="fw-medium">
        <span style={rosterSatirStili(row)}>{row.ad} {row.soyad}</span>
        {row.yasakli && <Badge color="danger" className="ms-2 fw-normal">{t("rosterEylemleri.girisiYasakli")}</Badge>}
        <IliskiBitisRozeti record={row} className="ms-2" />
      </div>
      <div className="small text-muted">{t("rosterEylemleri.kisiId")}{" "}{row.personId}</div>
      {bitis.sonlandirildi && (
        <div className="small text-muted" style={{ whiteSpace: "normal", maxWidth: 340 }}>
          {[bitis.tarih, bitis.bitirenAdi, bitis.kaynakAdi].filter(Boolean).join(" · ")}
          {bitis.sebep && <span className="fst-italic"> — {bitis.sebep}</span>}
        </div>
      )}
    </>
  );
}

/**
 * Eylem hücresinin içeriği.
 *   · aktif satır      → [Fuara Görevlendir (yeşil)] + [İlişkiyi Sonlandır (kırmızı)]
 *   · sonlandırılmış   → [Geri Al] yalnızca `geriAlinabilir` true ise, aksi halde HİÇBİR ŞEY
 *                        (rozetin başlığı sonlandırmanın kaynağını zaten söylüyor)
 */
export function RosterSatirEylemleri({ row, busy, fuarId, onGorevlendir, onSonlandir, onGeriAl }) {
  const { t } = useTranslation("components");
  if (iliskiBitisMeta(row).sonlandirildi) {
    if (row.geriAlinabilir !== true) return null;
    return (
      <Button
        size="sm" color="warning" outline disabled={busy}
        title={t("rosterEylemleri.sonlandirilanIliskiyiGeriAl")}
        onClick={() => onGeriAl(row)}
      >
        <Undo2 size={14} className="me-1" />{t("rosterEylemleri.geriAl")}
      </Button>
    );
  }
  return (
    <>
      {row.fuaraAtanabilir && (
        <Button
          size="sm" color="green" className="me-1" disabled={busy}
          onClick={() => onGorevlendir(row)}
          title={fuarId ? t("rosterEylemleri.fuarIcinGorevlendir", { fuarId }) : t("rosterEylemleri.onceFuarSecin")}
        >
          <UserCheck size={14} className="me-1" />{t("rosterEylemleri.fuaraGorevlendir")}
        </Button>
      )}
      {/* #81: sunucu `sonlandirilabilir: false` derse (bu yetkisi olmayan personel) düğme çizilmez. */}
      {row.sonlandirilabilir !== false && (
        <Button
          size="sm" color="danger" disabled={busy}
          title={t("rosterEylemleri.buFirmaIleIliskiyiSonlandir")}
          onClick={() => onSonlandir(row)}
        >
          <UserMinus size={14} className="me-1" />{t("rosterEylemleri.iliskiyiSonlandir")}
        </Button>
      )}
    </>
  );
}

/** İki pencerenin ortak "Sebep (opsiyonel)" alanı + sayaç. */
function SebepAlani({ value, onChange }) {
  const { t } = useTranslation("components");
  const uzun = value.length > BITIS_SEBEBI_MAX;
  return (
    <>
      <Label className="small mb-1">{t("rosterEylemleri.sebep")}{" "}<span className="text-muted">{t("rosterEylemleri.opsiyonel")}</span></Label>
      <Input
        type="textarea" rows={3} value={value} maxLength={BITIS_SEBEBI_MAX + 50}
        onChange={(e) => onChange(e.target.value)}
        invalid={uzun}
      />
      <div className={`small mt-1 ${uzun ? "text-danger" : "text-muted"}`}>
        {value.length} / {BITIS_SEBEBI_MAX}
      </div>
    </>
  );
}

/**
 * "İlişkiyi sonlandır" onayı. Sebep OPSİYONEL (bir reddin `red_sebebi`sinin aksine) — boş bırakılırsa
 * gönderilmiyor. Kim sonlandırdı ve hangi taraftan, oturumdan yazılır.
 * `kadroLink`: firma portalında "fuardan ayrıca kaldırın" uyarısı Personel sayfasına bağlanır
 * (denetmenin o route'a erişimi yok, orada düz metin kalır).
 */
export function IliskiSonlandirModal({ row, busy, onClose, onConfirm, kadroLink = false }) {
  return (
    <SonlandirIcerik
      key={row ? `son-${row.rosterId}` : "son-kapali"}
      row={row} busy={busy} onClose={onClose} onConfirm={onConfirm} kadroLink={kadroLink}
    />
  );
}

function SonlandirIcerik({ row, busy, onClose, onConfirm, kadroLink }) {
  const { t } = useTranslation("components");
  const [sebep, setSebep] = useState("");
  const uzun = sebep.length > BITIS_SEBEBI_MAX;
  return (
    <Modal isOpen={!!row} toggle={onClose}>
      <ModalHeader toggle={onClose}>{t("rosterEylemleri.firmaIleIliskiyiSonlandir")}</ModalHeader>
      <ModalBody>
        {row && (
          <>
            <p>
              {/* `row.firmaAdi`: kişi sayfasının Firmalar kartı (#266) birden çok firmayı aynı sayfada gösterir. */}
              {row.firmaAdi
                ? <><strong>{row.firmaAdi}</strong>{" "}{t("rosterEylemleri.firmasininPersonelListesinden")}{" "}<strong>{row.ad} {row.soyad}</strong>{" "}{t("rosterEylemleri.cikarilacak")}</>
                : <><strong>{row.ad} {row.soyad}</strong>{" "}{t("rosterEylemleri.buFirmaninPersonelListesindenCikarilacak")}</>}
              {t("rosterEylemleri.kayitSilinmezGecmisGorunurKalir")}
            </p>
            <Alert fade={false} color="warning" className="small d-flex gap-2">
              <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
              <div>
                <strong>{t("rosterEylemleri.mevcutFuarGorevlendirmeleriBuIslemle")}</strong>{" "}{t("rosterEylemleri.kisiGorevliOlduguFuarlarinKadrosunda")}
                {kadroLink && (
                  <> — <Link to="/Firma/Personel">{t("rosterEylemleri.personel")}</Link>{" "}{t("rosterEylemleri.sayfasindanOFuardakiKaydiniKaldirin")}</>
                )}.
              </div>
            </Alert>
            <SebepAlani value={sebep} onChange={setSebep} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("rosterEylemleri.vazgec")}</Button>
        <Button color="danger" onClick={() => onConfirm(sebep)} disabled={busy || uzun}>
          {busy ? <Spinner size="sm" /> : t("rosterEylemleri.sonlandir")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * "İlişkiyi geri al" onayı (backend #109). Onaydan önce neyin geri alınacağı görünür: kim, ne zaman,
 * hangi taraf, hangi sebeple sonlandırmıştı. Sebep OPSİYONEL — "bu bir hataydı" bilgisi geçmişe yazılır.
 */
export function IliskiGeriAlModal({ row, busy, onClose, onConfirm }) {
  return (
    <GeriAlIcerik
      key={row ? `geri-${row.rosterId}` : "geri-kapali"}
      row={row} busy={busy} onClose={onClose} onConfirm={onConfirm}
    />
  );
}

function GeriAlIcerik({ row, busy, onClose, onConfirm }) {
  const { t } = useTranslation("components");
  const [sebep, setSebep] = useState("");
  const uzun = sebep.length > BITIS_SEBEBI_MAX;
  const bitis = iliskiBitisMeta(row);
  return (
    <Modal isOpen={!!row} toggle={onClose}>
      <ModalHeader toggle={onClose}>{t("rosterEylemleri.iliskiyiGeriAl")}</ModalHeader>
      <ModalBody>
        {row && (
          <>
            <p>
              {row.firmaAdi
                ? <><strong>{row.firmaAdi}</strong>{" "}{t("rosterEylemleri.firmasininPersonelListesine")}{" "}<strong>{row.ad} {row.soyad}</strong>{" "}{t("rosterEylemleri.yenidenAlinacak")}</>
                : <><strong>{row.ad} {row.soyad}</strong>{" "}{t("rosterEylemleri.buFirmaninPersonelListesineYeniden")}</>}
            </p>
            {bitis.sonlandirildi && (
              <div className="small text-muted mb-3">
                {t("rosterEylemleri.sonlandirma")}{" "}{[bitis.tarih, bitis.bitirenAdi, bitis.kaynakAdi].filter(Boolean).join(" · ") || "—"}
                {bitis.sebep && <span className="fst-italic"> — {bitis.sebep}</span>}
              </div>
            )}
            <SebepAlani value={sebep} onChange={setSebep} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("rosterEylemleri.vazgec")}</Button>
        <Button color="warning" onClick={() => onConfirm(sebep)} disabled={busy || uzun}>
          {busy ? <Spinner size="sm" /> : t("rosterEylemleri.geriAl")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
